import type { OrderScope } from '@posilka/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMyOrders } from '@posilka/client/api/orders';
import { OrderCard } from '@/components/OrderCard';
import { Segmented } from '@/components/Segmented';
import { EmptyState, ErrorState, ListSkeleton } from '@/components/States';

export function MyOrdersPage() {
  const { t } = useTranslation();
  const [scope, setScope] = useState<OrderScope>('active');
  const q = useMyOrders(scope);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <h1 className="mb-3 px-1 font-display text-2xl font-bold tracking-tight">
        {t('nav.orders')}
      </h1>
      <div className="mb-4">
        <Segmented<OrderScope>
          value={scope}
          onChange={setScope}
          options={[
            { value: 'active', label: t('orders.active') },
            { value: 'finished', label: t('orders.finished') },
          ]}
        />
      </div>
      {q.isPending ? (
        <ListSkeleton />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          text={t(scope === 'active' ? 'orders.emptyActive' : 'orders.emptyFinished')}
          action={
            scope === 'active' && (
              <Link to="/orders/new" className="btn-secondary mt-2">
                {t('home.sendParcel')}
              </Link>
            )
          }
        />
      ) : (
        <>
          {items.map((o) => (
            <OrderCard key={o.id} order={o} to={`/orders/${o.id}`} />
          ))}
          {q.hasNextPage && (
            <button
              type="button"
              className="btn-secondary w-full"
              disabled={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
            >
              {t('common.loadMore')}
            </button>
          )}
        </>
      )}
    </>
  );
}
